import React, { useState } from 'react';
import { useMobile } from '../hooks/useMobile';
import { useForm } from 'react-hook-form';
import { calculatorValidationSchema } from '../Schemas/calculator-validation';
import { yupResolver } from '@hookform/resolvers/yup';
import { postNewLead } from '../components/API/lead';
import makeStyles from '@material-ui/core/styles/makeStyles';
import LoadingButton from '../components/lowLevel/LoadingButton';
import SeasonalProduct from '../components/Calculator/SeasonalProduct';
import RecentlySales from '../components/Calculator/RecentlySales';
import AverageUnitPerPrice from '../components/Calculator/AverageUnitPerPrice';
import AverageUnitCost from '../components/Calculator/AverageUnitCost';
import Banner from '../components/BuildingBlocks/GetStarted/Banner';
import CalculateResult from '../components/Calculator/CalculateResult';
import InputTextField from '../components/BuildingBlocks/Form/TextField';
import Typography from '@material-ui/core/Typography/Typography';

const useStyles = makeStyles((theme: any) => ({
  container: {
    backgroundColor: theme.palette.brand.white,
    justifyContent: 'center',
    textAlign: 'start',
    alignSelf: 'center',

    maxWidth: ({ mobile }: any) => (mobile ? 350 : 750),
    textAlignLast: 'left',
    paddingLeft: ({ mobile }: any) => !mobile && theme.spacing(12),
    paddingRight: ({ mobile }: any) => !mobile && theme.spacing(12),
    boxShadow: ({ mobile }: any) =>
      !mobile &&
      '-2px 0px 0px 0px rgba(0, 0, 0, 0.08), 2px 0px 0px 0px rgba(0, 0, 0, 0.08)',
  },
  title: {
    fontWeight: 800,
    marginBottom: theme.spacing(2),
    color: theme.palette.brand.greenDark,
  },
  button: {
    height: theme.spacing(8),
  },
}));

function isBetween(n, a, b) {
  return (n - a) * (n - b) <= 0;
}

export default function Calculator() {
  const mobile = useMobile();
  const classes = useStyles({ mobile });
  const [netCapital, setNetCapital] = useState('');
  const [netCapitalMin, setNetCapitalMin] = useState(0);
  const [netCapitalMax, setNetCapitalMax] = useState(0);
  const [time, setTime] = useState('');
  const [showResult, setShowResult] = useState(false);

  const formOptions = {
    resolver: yupResolver(calculatorValidationSchema),
  };

  const {
    register,
    formState: { errors },
    setValue,
    handleSubmit,
    control,
  } = useForm(formOptions);

  const onSubmit = async (data) => {
    const isSeasonal = data.seasonal === 'true';
    const annualRevenueResult = data.threeMonthsSales * (isSeasonal ? 2.5 : 4);
    const projectedRevenueResult =
      data.threeMonthsSales * (isSeasonal ? 2.5 : 4);
    if (isBetween(annualRevenueResult, 100, 500000)) {
      setNetCapital('$264,546 - $357,915');
      setNetCapitalMin(264546);
      setNetCapitalMax(357915);
      setTime('15 - 31');
    } else if (isBetween(annualRevenueResult, 500000, 1000000)) {
      setNetCapital('$358,882 - $485,547');
      setNetCapitalMin(358882);
      setNetCapitalMax(485547);
      setTime('10 - 26');
    } else if (annualRevenueResult > 500000) {
      setNetCapital(' $1,433,264 - $1,939,121');
      setNetCapitalMin(1433264);
      setNetCapitalMax(1939121);
      setTime('7 - 18');
    }
    setShowResult(true);

    try {
      data.annualRevenue = annualRevenueResult;
      data.projectedRevenue = projectedRevenueResult;
      const id = window.localStorage.getItem('leadId');
      if (id && id !== 'undefined') data.id = id;
      const res = await postNewLead(data);
      if (!id) window.localStorage.setItem('leadId', res?.id);
    } catch (e) {}
  };

  return (
    <>
      <Banner />

      <div className={classes.container}>
        <Typography variant={'h4'} className={classes.title}>
          How far is your eCommerce business from $10,000,000 is sales?
        </Typography>
        <form onSubmit={handleSubmit(onSubmit)}>
          <RecentlySales register={register} errors={errors} />
          <SeasonalProduct setValue={setValue} control={control} />
          <AverageUnitPerPrice register={register} errors={errors} />
          <AverageUnitCost register={register} errors={errors} />

          <InputTextField
            errors={errors}
            label={'E-mail'}
            name={'email'}
            TextFieldProps={{
              ...register('email'),
            }}
          />

          <LoadingButton
            type={'submit'}
            color={'primary'}
            variant={'contained'}
            fullWidth
            className={classes.button}
            loading={false}
          >
            See my path to 8 figures
          </LoadingButton>
        </form>
      </div>
      {showResult && (
        <CalculateResult
          netCapital={netCapital}
          netCapitalMin={netCapitalMin}
          netCapitalMax={netCapitalMax}
          time={time}
        />
      )}
    </>
  );
}
